import clsx from 'clsx'
import { type FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import AgoraRTC, {
  LocalUser,
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteUsers,
  type ILocalVideoTrack,
} from 'agora-rtc-react'
import { geraTokenAutenticacaoVideo } from '../../../../auth/core/_requests'
import { useAuth } from '../../../../auth'
import {
  geraTokenVideoPaciente,
  isAcessoNegadoVideo,
} from '../../../../tele-consulta/core/_requests'
import { showAlert } from '../../../../../../_metronic/helpers'
import { InfoAlert } from '../../../../../components'
import { type VideoAtendimentoAgoraProps } from '../types'
import { useAtendimentoChat } from '../hooks/useAtendimentoChat'
import { useVirtualBackground } from '../hooks/useVirtualBackground'
import { useDeviceState } from '../hooks/useDeviceState'
import { useGravacao } from '../hooks/useGravacao'
import { CaptionOverlay } from './CaptionOverlay'
import { ChatPanel } from './ChatPanel'
import { VideoControls } from './VideoControls'
import { VirtualBackgroundMenu } from './VirtualBackgroundMenu'
import { VirtualBackgroundModal } from './VirtualBackgroundModal'
import { RemoteUserCard } from './RemoteUserCard'
import styles from './styles/AgoraVideoContainer.module.css'
import { VideoCallIcon } from './videoCallIcons'

const isSystemAgent = (uid: any) => Number(uid) >= 27848 && Number(uid) <= 29999

export const AgoraVideoContainer: FC<VideoAtendimentoAgoraProps> = ({
  idAtendimento,
  idPessoa,
  isPaciente,
  pacienteNome,
  chatOpen: propChatOpen,
  setChatOpen: propSetChatOpen,
  unreadCount: propUnreadCount,
  isSttEnabled,
  sttPendente,
  onToggleStt,
  captionText = '',
}) => {
  const { isEnabled: isMicEnabled, toggle: toggleMic } = useDeviceState('agora-mic-enabled')
  const { isEnabled: isVideoEnabled, toggle: toggleVideo } = useDeviceState('agora-video-enabled')
  const [token, setToken] = useState('')
  const [calling, setCalling] = useState(false)
  const [acessoNegado, setAcessoNegado] = useState(false)
  const [localChatOpen, setLocalChatOpen] = useState(false)
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)
  const { auth, currentUser } = useAuth()

  const [isMaximized, setIsMaximized] = useState(false)
  const [patientVolume, setPatientVolume] = useState(100)
  const [isScreenSharing, setIsScreenSharing] = useState(false)
  const [screenTrack, setScreenTrack] = useState<ILocalVideoTrack | null>(null)
  const { isGravacaoAtiva, gravacaoPendente, toggleGravacao } = useGravacao(
    idAtendimento,
    isPaciente
  )

  useEffect(() => {
    if (!isScreenSharing) return

    let cancelled = false
    let createdTrack: ILocalVideoTrack | null = null

    AgoraRTC.createScreenVideoTrack({}, 'disable')
      .then((track) => {
        if (cancelled) {
          track.close()
          return
        }

        createdTrack = track
        track.on('track-ended', () => setIsScreenSharing(false))
        setScreenTrack(track)
      })
      .catch((error: any) => {
        if (!cancelled) {
          setIsScreenSharing(false)
          if (error?.name !== 'NotAllowedError') {
            showAlert({
              title: 'Atenção',
              text: 'Não foi possível compartilhar a tela. Verifique as permissões do navegador.',
              icon: 'warning',
            })
          }
        }
      })

    return () => {
      cancelled = true
      if (createdTrack) {
        createdTrack.close()
      }
      setScreenTrack(null)
    }
  }, [isScreenSharing])

  useEffect(() => {
    if (!isMaximized) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isMaximized])

  const chatOpen = propChatOpen !== undefined ? propChatOpen : localChatOpen
  const setChatOpen = propSetChatOpen !== undefined ? propSetChatOpen : setLocalChatOpen

  useEffect(() => {
    if (!chatOpen) {
      setPortalTarget(null)
      return
    }

    setPortalTarget(document.getElementById('atendimento-chat-container'))
  }, [chatOpen])

  useEffect(() => {
    let isMounted = true
    const pedido = isPaciente ? geraTokenVideoPaciente() : geraTokenAutenticacaoVideo(idAtendimento)

    pedido
      .then((tokenResponse) => {
        if (!isMounted) return

        setAcessoNegado(false)
        setToken(tokenResponse.token)
        setCalling(true)
      })
      .catch((error: unknown) => {
        if (isMounted) {
          setCalling(false)
          setAcessoNegado(isAcessoNegadoVideo(error))
        }
      })

    return () => {
      isMounted = false
    }
  }, [idAtendimento, idPessoa, isPaciente])

  useJoin(
    {
      appid: process.env.REACT_APP_AGORA_APP_ID!,
      channel: idAtendimento.toString(),
      token,
      uid: idPessoa,
    },
    calling
  )

  const { localMicrophoneTrack } = useLocalMicrophoneTrack(isMicEnabled)
  const { localCameraTrack } = useLocalCameraTrack(isVideoEnabled)

  const videoTrackToPublish = isScreenSharing && screenTrack ? screenTrack : localCameraTrack
  usePublish([localMicrophoneTrack, videoTrackToPublish])

  const allRemoteUsers = useRemoteUsers()
  const remoteUsers = allRemoteUsers.filter((user) => !isSystemAgent(user.uid))
  const patientJoined = remoteUsers.length > 0

  const {
    messages,
    unreadCount: chatUnreadCount,
    sendMessage,
    aviso: chatAviso,
  } = useAtendimentoChat({
    idAtendimento,
    accessToken: isPaciente ? undefined : auth?.accessToken,
    currentUser,
    chatOpen,
    isPaciente,
  })

  const unreadCount = propUnreadCount !== undefined ? propUnreadCount : chatUnreadCount

  const {
    processor,
    backgroundType,
    showBackgroundMenu,
    setShowBackgroundMenu,
    showBackgroundModal,
    previewBackground,
    setPreviewBackground,
    customBackgrounds,
    selectedBackgroundUrl,
    fileInputRef,
    applyVirtualBackground,
    handleFileUpload,
    removeCustomBackground,
    applyPreview,
    closeBackgroundModal,
    openBackgroundModal,
  } = useVirtualBackground({
    isPaciente,
    localCameraTrack,
  })

  const toggleChat = () => setChatOpen(!chatOpen)
  const toggleMaximize = () => setIsMaximized((maximized) => !maximized)

  return (
    <>
      <div
        className={clsx(
          `w-100 h-100 d-flex flex-column align-items-center justify-content-center overflow-hidden bg-black ${styles.videoContainer}`,
          isMaximized ? 'position-fixed top-0 start-0' : 'position-relative'
        )}
        style={isMaximized ? { zIndex: 9999 } : undefined}
      >
        {isGravacaoAtiva && (
          <div
            role='status'
            aria-live='polite'
            className={`position-absolute top-0 start-0 m-3 d-flex align-items-center gap-2 rounded px-3 py-1 text-white fw-bold fs-8 ${styles.recordingBadge}`}
          >
            <i className={`${VideoCallIcon.RecordingDot} text-white fs-9`} aria-hidden='true' />
            <span>Gravando</span>
          </div>
        )}

        {remoteUsers.map((user) => (
          <RemoteUserCard key={user.uid} user={user} patientVolume={patientVolume} />
        ))}

        <LocalUser
          audioTrack={localMicrophoneTrack}
          cameraOn={isScreenSharing || isVideoEnabled}
          micOn={isMicEnabled}
          videoTrack={videoTrackToPublish}
          playAudio={false}
          className={clsx(styles.localPreview, !patientJoined && 'd-none')}
        />

        {!isVideoEnabled && patientJoined && (
          <div
            className={`position-absolute d-flex align-items-center justify-content-center ${styles.localOff}`}
          >
            <i className={`${VideoCallIcon.VideoOff} ${styles.localOffIcon}`} />
          </div>
        )}

        {acessoNegado && (
          <div
            className={`position-relative z-index-3 w-100 h-100 d-flex align-items-center justify-content-center p-5 ${styles.waitingOverlay}`}
          >
            <InfoAlert
              variant='danger'
              role='alert'
              title='Você não tem autorização para entrar nesta videochamada.'
              className='mb-0 mw-600px w-100'
            >
              {isPaciente
                ? 'Acesse novamente pelo link recebido.'
                : 'Confira se você tem acesso a este atendimento.'}
            </InfoAlert>
          </div>
        )}

        {!patientJoined && !acessoNegado && (
          <div
            className={`position-relative z-index-3 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-white ${styles.waitingOverlay}`}
          >
            <span className='fs-2 fw-semibold mb-4'>
              Aguardando {isPaciente ? 'atendimento' : 'paciente'}...
            </span>
            <span>
              <i className={`${VideoCallIcon.Spinner} fa-spin`} />
            </span>
          </div>
        )}

        {/* Dentro do container: no modo maximizado ele vira position-fixed (z-index 9999) e cobriria uma legenda irmã. */}
        <CaptionOverlay text={captionText} />

        <VideoControls
          isMicEnabled={isMicEnabled}
          isVideoEnabled={isVideoEnabled}
          isPaciente={isPaciente}
          hasProcessor={Boolean(processor)}
          backgroundType={backgroundType}
          showBackgroundMenu={showBackgroundMenu}
          chatOpen={chatOpen}
          unreadCount={unreadCount}
          onToggleMic={toggleMic}
          onToggleVideo={toggleVideo}
          onToggleBackgroundMenu={() => setShowBackgroundMenu((show) => !show)}
          onToggleChat={toggleChat}
          isScreenSharing={isScreenSharing}
          onToggleScreenShare={() => setIsScreenSharing((enabled) => !enabled)}
          isMaximized={isMaximized}
          onToggleMaximize={toggleMaximize}
          patientVolume={patientVolume}
          onChangeVolume={setPatientVolume}
          isGravacaoAtiva={isGravacaoAtiva}
          gravacaoPendente={gravacaoPendente}
          onToggleGravacao={toggleGravacao}
          isSttEnabled={isSttEnabled}
          sttPendente={sttPendente}
          onToggleStt={onToggleStt}
        />

        {!isPaciente && showBackgroundMenu && (
          <VirtualBackgroundMenu
            backgroundType={backgroundType}
            selectedBackgroundUrl={selectedBackgroundUrl}
            onApplyBackground={applyVirtualBackground}
            onOpenGallery={openBackgroundModal}
          />
        )}

        {!isPaciente && showBackgroundModal && (
          <VirtualBackgroundModal
            customBackgrounds={customBackgrounds}
            previewBackground={previewBackground}
            selectedBackgroundUrl={selectedBackgroundUrl}
            fileInputRef={fileInputRef}
            onClose={closeBackgroundModal}
            onClearPreview={() => setPreviewBackground(null)}
            onFileUpload={handleFileUpload}
            onApplyPreview={applyPreview}
            onApplyBackground={applyVirtualBackground}
            onRemoveBackground={removeCustomBackground}
          />
        )}

        <div
          className={clsx(
            'position-absolute z-index-3 d-flex align-items-center fw-semibold rounded-pill shadow px-4 py-1',
            styles.status,
            patientJoined && styles.connected
          )}
        >
          <i
            className={clsx(
              !isMicEnabled
                ? `${VideoCallIcon.MicOff} text-danger`
                : `${VideoCallIcon.Mic} text-success`,
              'me-2'
            )}
          />
          <i
            className={clsx(
              isVideoEnabled
                ? `${VideoCallIcon.Video} text-success`
                : `${VideoCallIcon.VideoOff} text-danger`
            )}
          />
          {patientJoined && <span className={`ms-4 ${styles.statusText}`}>Conectado</span>}
        </div>
      </div>

      {chatOpen &&
        portalTarget &&
        createPortal(
          <ChatPanel
            messages={messages}
            currentUser={currentUser}
            pacienteNome={pacienteNome}
            onSendMessage={sendMessage}
            onClose={() => setChatOpen(false)}
            aviso={chatAviso}
          />,
          portalTarget
        )}
    </>
  )
}
